'use client';

import * as React from 'react';
import { useComponentRenderer } from "../../utils/useComponentRenderer.js";
import { useRadioRootContext } from "../root/RadioRootContext.js";
import { customStyleHookMapping } from "../utils/customStyleHookMapping.js";
import { useOpenChangeComplete } from "../../utils/useOpenChangeComplete.js";
import { useForkRef } from "../../utils/useForkRef.js";
import { useTransitionStatus } from "../../utils/useTransitionStatus.js";

/**
 * Indicates whether the radio button is selected.
 * Renders a `<span>` element.
 *
 * Documentation: [Base UI Radio](https://base-ui.com/react/components/radio)
 */
export const RadioIndicator = /*#__PURE__*/React.forwardRef(function RadioIndicator(props, forwardedRef) {
  const {
    render,
    className,
    keepMounted = false,
    ...otherProps
  } = props;
  const rootState = useRadioRootContext();
  const rendered = rootState.checked;
  const {
    transitionStatus,
    setMounted
  } = useTransitionStatus(rendered);
  const state = React.useMemo(() => ({
    ...rootState,
    transitionStatus
  }), [rootState, transitionStatus]);
  const indicatorRef = React.useRef(null);
  const mergedRef = useForkRef(forwardedRef, indicatorRef);
  const {
    renderElement
  } = useComponentRenderer({
    render: render ?? 'span',
    ref: mergedRef,
    className,
    state,
    extraProps: otherProps,
    customStyleHookMapping
  });
  useOpenChangeComplete({
    open: rendered,
    ref: indicatorRef,
    onComplete() {
      if (!rendered) {
        setMounted(false);
      }
    }
  });
  const shouldRender = keepMounted || rendered;
  if (!shouldRender) {
    return null;
  }
  return renderElement();
});
if (process.env.NODE_ENV !== "production") RadioIndicator.displayName = "RadioIndicator";